/**
 * PresenceTracker — always-on, low-frequency ambient location tracking.
 *
 * Purpose: keep a coarse, up-to-date idea of where the user is so the app can
 * surface nearby emergency alerts and business recommendations. This is NOT a
 * high-fidelity trail recorder (that's GeoTracker) — it is deliberately
 * sparse to protect battery and storage.
 *
 * Sampling is DISTANCE-BASED, not millisecond-based. GPS hardware only yields
 * a genuinely new fix about once per second, and vicinity features operate at
 * the scale of hundreds of metres, so we record a fix only when the user has
 * moved a meaningful distance, with a slow heartbeat when stationary:
 *
 *   Profile      New fix when moved…   Stationary heartbeat   Throttle   Drop worse than
 *   foreground   ≥ 30 m                ≤ 60 s                 ≥ 5 s      80 m accuracy
 *   background   ≥ 120 m               ≤ 5 min                ≥ 15 s     120 m accuracy
 *
 * The tracker switches to the foreground profile while the app is visible and
 * the background profile while it is hidden. On native (Capacitor) builds a
 * background-geolocation plugin can feed fixes into ingestFix() so tracking
 * continues when the app is backgrounded; in the browser, watchPosition
 * pauses when the tab is hidden (a platform limitation).
 *
 * Storage is provided by the host through an adapter:
 *   storage.addFix(fix): Promise<void>   // fix: normalized shape below
 *
 * Normalized fix: { clientUuid, lon, lat, accuracy, altitude, heading, speed,
 *                   mode, recordedAt }
 *
 * @module presence/PresenceTracker
 */

import { haversineMeters } from '../geotracker/geo-utils.js';

export const PROFILES = {
  foreground: { minDistanceM: 30,  heartbeatMs: 60000,  minIntervalMs: 5000,  maxAccuracyM: 80 },
  background:  { minDistanceM: 120, heartbeatMs: 300000, minIntervalMs: 15000, maxAccuracyM: 120 },
};

export class PresenceTracker {
  /**
   * @param {object} opts
   * @param {object} opts.storage           adapter with addFix(fix)
   * @param {Geolocation} [opts.geolocation] inject navigator.geolocation (tests)
   * @param {object} [opts.profiles]         override PROFILES
   */
  constructor(opts = {}) {
    this.storage = opts.storage || null;
    this._geo = opts.geolocation ||
      (typeof navigator !== 'undefined' ? navigator.geolocation : null);
    this.profiles = opts.profiles || PROFILES;

    this._running = false;
    this._watchId = null;
    this._mode = 'foreground';        // active profile key
    this._lastRecorded = null;        // { lon, lat, at }
    this._lastFix = null;             // most recent normalized fix (any quality)
    this._listeners = new Set();
    this._onVisibility = () => this._applyVisibilityProfile();
  }

  get isSupported() { return !!this._geo; }
  get isRunning() { return this._running; }
  get lastFix() { return this._lastFix; }
  get mode() { return this._mode; }

  /** Subscribe to recorded fixes. Returns an unsubscribe function. */
  onFix(cb) { this._listeners.add(cb); return () => this._listeners.delete(cb); }

  /**
   * Begin tracking. No-op if already running or unsupported. The caller is
   * responsible for having obtained user consent first.
   */
  start() {
    if (this._running || !this._geo) return;
    if (!this.storage) throw new Error('PresenceTracker: no storage adapter');
    this._running = true;
    this._applyVisibilityProfile();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this._onVisibility);
    }
    this._startWatch();
    console.log('[Presence] Tracking started (profile:', this._mode + ')');
  }

  /** Stop tracking and release the geolocation watch. */
  stop() {
    if (!this._running) return;
    this._running = false;
    this._stopWatch();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this._onVisibility);
    }
    console.log('[Presence] Tracking stopped');
  }

  /**
   * Feed an externally-sourced fix (e.g. from a native background plugin)
   * through the same filter + storage pipeline. `pos` is a browser-style
   * GeolocationPosition or a { coords, timestamp } object.
   */
  ingestFix(pos) { this._onPosition(pos); }

  // ---------- internals ----------

  _profile() { return this.profiles[this._mode]; }

  _applyVisibilityProfile() {
    const hidden = typeof document !== 'undefined' && document.hidden;
    const next = hidden ? 'background' : 'foreground';
    if (next !== this._mode) {
      this._mode = next;
      // Restart the watch so the new accuracy/interval hints take effect.
      if (this._running && this._watchId != null) { this._stopWatch(); this._startWatch(); }
    }
  }

  _startWatch() {
    if (this._watchId != null || !this._geo) return;
    this._watchId = this._geo.watchPosition(
      (pos) => this._onPosition(pos),
      (err) => console.warn('[Presence] geolocation error:', err?.message || err),
      { enableHighAccuracy: false, timeout: 20000, maximumAge: 15000 }
    );
  }

  _stopWatch() {
    if (this._watchId != null && this._geo) this._geo.clearWatch(this._watchId);
    this._watchId = null;
  }

  async _onPosition(pos) {
    const fix = PresenceTracker.normalize(pos, this._mode);
    this._lastFix = fix;

    const p = this._profile();
    const now = Date.parse(fix.recordedAt) || Date.now();

    // Drop low-quality fixes (unless it's our very first).
    if (this._lastRecorded && p.maxAccuracyM > 0 && fix.accuracy != null && fix.accuracy > p.maxAccuracyM) {
      return;
    }

    let keep = false;
    if (!this._lastRecorded) {
      keep = true;                                // always record the first fix
    } else {
      const elapsed = now - this._lastRecorded.at;
      if (elapsed < p.minIntervalMs) return;      // throttle
      const stepM = haversineMeters(this._lastRecorded.lon, this._lastRecorded.lat, fix.lon, fix.lat);
      if (stepM >= p.minDistanceM || elapsed >= p.heartbeatMs) keep = true;
    }
    if (!keep) return;

    this._lastRecorded = { lon: fix.lon, lat: fix.lat, at: now };
    try {
      await this.storage.addFix(fix);
      for (const cb of this._listeners) { try { cb(fix); } catch (e) { console.error(e); } }
    } catch (err) {
      console.warn('[Presence] storage.addFix failed:', err?.message || err);
    }
  }

  /** Normalize a GeolocationPosition into the presence-fix shape. */
  static normalize(pos, mode = 'foreground') {
    const c = pos?.coords || {};
    const num = (v) => (v != null && !Number.isNaN(v) ? v : null);
    return {
      clientUuid: PresenceTracker.uuid(),
      lon: c.longitude,
      lat: c.latitude,
      accuracy: num(c.accuracy),
      altitude: num(c.altitude),
      heading: num(c.heading),
      speed: num(c.speed),
      mode,
      recordedAt: new Date(pos?.timestamp || Date.now()).toISOString(),
    };
  }

  static uuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
      const r = (Math.random() * 16) | 0;
      const v = ch === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}

export default PresenceTracker;
